import { CashFlowParams, CashFlowItem } from './types';

/**
 * Generates cash flow data for financial projections
 * @param params - Cash flow generation parameters
 * @returns Array of cash flow data items
 */
export function generateCashFlow(params: CashFlowParams): CashFlowItem[] {
  const {
    initialInvestment,
    annualRevenue,
    annualCosts,
    growthRate,
    discountRate,
    years
  } = params;

  const cashFlowData: CashFlowItem[] = [];

  // Initial investment in year 0
  cashFlowData.push({
    year: 0,
    revenue: 0,
    costs: initialInvestment,
    cashFlow: -initialInvestment,
    discountedCashFlow: -initialInvestment
  });

  // Project cash flows for subsequent years
  for (let year = 1; year <= years; year++) {
    const revenue = annualRevenue * Math.pow(1 + growthRate, year - 1);
    const costs = annualCosts * Math.pow(1 + growthRate * 0.5, year - 1); // Costs grow slower than revenue
    const cashFlow = revenue - costs;
    const discountedCashFlow = cashFlow / Math.pow(1 + discountRate, year);

    cashFlowData.push({
      year,
      revenue,
      costs,
      cashFlow,
      discountedCashFlow
    });
  }

  return cashFlowData;
}

/**
 * Calculates Net Present Value (NPV) from cash flow data
 * @param cashFlowData - Array of cash flow data items
 * @returns The calculated NPV
 */
export function calculateNPV(cashFlowData: CashFlowItem[]): number {
  return cashFlowData.reduce((sum, item) => sum + item.discountedCashFlow, 0);
}

/**
 * Calculates Internal Rate of Return (IRR) from cash flow data
 * Using the approximation method for simplicity
 * @param cashFlowData - Array of cash flow data items
 * @returns The calculated IRR as a decimal (e.g., 0.15 for 15%)
 */
export function calculateIRR(cashFlowData: CashFlowItem[]): number {
  // Simple approximation method - in a real app would use Newton-Raphson or similar
  const cashFlows = cashFlowData.map(item => item.cashFlow);
  
  let irr = 0.1; // Initial guess
  let step = 0.05;
  let direction = 1;
  let previousNPV = Number.MAX_VALUE;
  
  // Simple iterative approximation
  for (let i = 0; i < 20; i++) {
    let npv = 0;
    for (let j = 0; j < cashFlows.length; j++) {
      npv += cashFlows[j] / Math.pow(1 + irr, j);
    }
    
    // If we're getting closer to zero
    if (Math.abs(npv) < Math.abs(previousNPV)) {
      previousNPV = npv;
      irr += step * direction;
    } else {
      // If we overshot, reverse direction and reduce step size
      direction *= -1;
      step /= 2;
      irr += step * direction;
    }
    
    // If we're close enough to zero
    if (Math.abs(npv) < 1000) {
      break;
    }
  }
  
  return Math.max(0, irr); // Ensure non-negative IRR
}

/**
 * Calculates the payback period from cash flow data
 * @param cashFlowData - Array of cash flow data items
 * @returns The calculated payback period in years
 */
export function calculatePaybackPeriod(cashFlowData: CashFlowItem[]): number {
  let cumulativeCashFlow = 0;
  
  for (let i = 0; i < cashFlowData.length; i++) {
    cumulativeCashFlow += cashFlowData[i].cashFlow;
    
    if (cumulativeCashFlow >= 0) {
      // If we exactly hit zero, return the current year
      if (cumulativeCashFlow === 0) {
        return cashFlowData[i].year;
      }
      
      // Otherwise, we need to interpolate between this year and the previous
      const previousCumulativeCashFlow = cumulativeCashFlow - cashFlowData[i].cashFlow;
      const fraction = Math.abs(previousCumulativeCashFlow) / 
                      Math.abs(cashFlowData[i].cashFlow);
      
      return cashFlowData[i].year - 1 + fraction;
    }
  }
  
  // If we never reach a positive cumulative cash flow, return a value beyond the project life
  return cashFlowData[cashFlowData.length - 1].year + 1;
}

/**
 * Calculate the profitability index
 * @param cashFlowData - Array of cash flow data items
 * @returns The calculated profitability index
 */
export function calculateProfitabilityIndex(cashFlowData: CashFlowItem[]): number {
  const initialInvestment = Math.abs(cashFlowData[0].cashFlow);
  const npv = calculateNPV(cashFlowData);
  
  return (npv + initialInvestment) / initialInvestment;
}

/**
 * Calculate annual forecast for a project
 * @param cashFlowData - Array of cash flow data items for multi-year projection
 * @returns Object with annual forecast data
 */
export function calculateAnnualForecast(cashFlowData: CashFlowItem[]): {
  totalRevenue: number;
  totalCosts: number;
  netCashFlow: number;
  endOfYearBalance: number;
} {
  // Filter out year 0 (investment) and extract first year of operations
  const operationalData = cashFlowData.filter(item => item.year > 0 && item.year <= 1);
  
  if (operationalData.length === 0) {
    return {
      totalRevenue: 0,
      totalCosts: 0,
      netCashFlow: 0,
      endOfYearBalance: -Math.abs(cashFlowData[0]?.cashFlow || 0)
    };
  }
  
  const firstYear = operationalData[0];
  const initialInvestment = Math.abs(cashFlowData[0]?.cashFlow || 0);
  
  return {
    totalRevenue: firstYear.revenue,
    totalCosts: firstYear.costs,
    netCashFlow: firstYear.cashFlow,
    endOfYearBalance: firstYear.cashFlow - initialInvestment
  };
}
